import { observer } from 'src/utils/mobx-react';
import { SelectionsSettingsForm } from 'src/domains/sportsbook/trading/view/eventDetails/markets/selectionsSettings/SelectionsSettingsForm';
import { arrayMove, List } from 'react-movable';
import React, { useRef, useState } from 'react';
import { action, computed, observable, makeAutoObservable } from 'mobx';
import styled from '@emotion/styled';
import { AppState, useAppStateContext } from 'src/AppState/AppState';
import { TypeId } from 'src_common/common/websocket2/type';
import { CriteriaType } from 'src_common/utils/sport/sort';
import { Permission } from 'src/AppState/PermissionGrants/PermissionGrants.types';

// eslint-disable-next-line no-restricted-imports
import { useAsObservableSource } from 'mobx-react-lite';

const ListElement = styled('div', { label: 'ListElement' })<{ isDragged: boolean }>`
    z-index: 1000;
    position: relative;
    padding: 8px 10px;
    background: #f3f3f3;
    border: 1px solid #eee;
    cursor: pointer;
    width: 100%;
    opacity: ${({ isDragged }): string => (isDragged === true ? '.5' : '1')};
`;

type SelectionsForSortListType = Array<{ display_order: number; selectionId: number }>;

class SortableMarketSelectionsState {
    @observable public eventId: number | null = null;
    @observable public marketId: number | null = null;

    public constructor(
        private readonly props: SortableMarketSelectionsPropsType,
        private readonly appState: AppState
    ) {
        makeAutoObservable(this);
    }

    @computed public get hasPermission(): boolean {
        return this.appState.permissionGrantsState.has(Permission.bettingOddsChange) === true;
    }
    @computed public get buildListForView(): SelectionsForSortListType {
        const out: SelectionsForSortListType = [];

        const marketModel = this.appState.sdkStaff.models.getMarket(this.props.marketId);
        if (marketModel !== null) {
            const rawSelections = marketModel.selectionWithSorting(this.props.order);
            rawSelections.forEach((selection) => {
                out.push({ display_order: selection.displayOrder ?? 0, selectionId: selection.id });
            });
        }

        return out;
    }

    public buildSelectionWithNewOrder = (
        selectionOrderList: SelectionsForSortListType
    ): { selectionsOrder: Record<number, { displayOrder: number }> } => {
        const out: { selectionsOrder: Record<number, { displayOrder: number }> } = { selectionsOrder: {} };
        const marketModel = this.appState.sdkStaff.models.getMarket(this.props.marketId);
        marketModel?.selections.forEach((selection) => {
            selectionOrderList.forEach((updatedSelection) => {
                if (
                    updatedSelection.selectionId === selection.id &&
                    updatedSelection.display_order !== selection.displayOrder
                ) {
                    out.selectionsOrder[selection.id] = { displayOrder: updatedSelection.display_order };
                }
            });
        });

        return out;
    };

    @action public updateSelectionOrderList = (list: SelectionsForSortListType): SelectionsForSortListType => {
        return list.map((selection, index) => {
            return {
                display_order: index + 1,
                selectionId: selection.selectionId,
            };
        });
    };

    @action public onOrderChangeHandler = async ({
        oldIndex,
        newIndex,
    }: {
        oldIndex: number;
        newIndex: number;
        targetRect: ClientRect;
    }): Promise<void> => {
        const marketModel = this.appState.sdkStaff.models.getMarket(this.props.marketId);
        if (marketModel !== null) {
            const withNewOrder = this.updateSelectionOrderList(arrayMove(this.buildListForView, oldIndex, newIndex));
            const data: { selectionsOrder: Record<number, { displayOrder: number }> } =
                this.buildSelectionWithNewOrder(withNewOrder);
            await this.appState.sdkStaff.event_reorder_selection({
                event_id: marketModel.eventId,
                market_id: TypeId.getMarketId(marketModel.id),
                requestBody: data,
            });
        }
    };
}

type SortableMarketSelectionsPropsType = {
    order: CriteriaType;
    marketId: TypeId.MarketId;
};

export const SortableMarketSelections = observer(
    'SortableMarketSelections',
    (propsIn: SortableMarketSelectionsPropsType) => {
        const props = useAsObservableSource(propsIn);
        const appState = useAppStateContext();
        const [state] = useState(() => new SortableMarketSelectionsState(props, appState));

        const listRef = useRef<HTMLDivElement>(null);

        return (
            <div ref={listRef}>
                {/* {hasPermissions === true ? null : (
                    <NonClickableOpacityWrapper
                        height={listHeight}
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                    />
                )} */}
                <List
                    lockVertically
                    values={state.buildListForView}
                    onChange={state.onOrderChangeHandler}
                    renderItem={({ value, props: itemProps, isDragged, index }) => {
                        itemProps.tabIndex = undefined;
                        return (
                            <ListElement
                                {...itemProps}
                                className='form'
                                isDragged={isDragged}
                            >
                                <SelectionsSettingsForm
                                    index={index ?? 0}
                                    key={value.selectionId}
                                    selectionId={value.selectionId}
                                    marketId={props.marketId}
                                />
                            </ListElement>
                        );
                    }}
                    renderList={({ children, props: ListProps, isDragged }) => (
                        <ul
                            {...ListProps}
                            style={{ cursor: isDragged ? 'grabbing' : 'inherit' }}
                        >
                            {children}
                        </ul>
                    )}
                />
            </div>
        );
    }
);
